import React from 'react';
import { Hexagon, ShoppingCart, MessageSquare, Bell, Shield, Lock, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { toast } from 'sonner';

export const ListingDetailModal = ({
  detailListing, setDetailListing, setBuyModal, setOfferForm, setShowOfferModal,
  setAlertForm, setShowAlertModal, setContactFor, setContactMessage, formatCurrency,
}) => (
  <Dialog open={!!detailListing} onOpenChange={() => setDetailListing(null)}>
    <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="listing-detail-modal">
      <DialogHeader><DialogTitle className="font-serif text-white">Asset Details</DialogTitle></DialogHeader>
      {detailListing && (
        <div className="space-y-5">
          <div className="h-56 rounded-lg overflow-hidden bg-[#0F0F0F]">
            {detailListing.image_url ? (
              <img src={detailListing.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${detailListing.image_url}` : detailListing.image_url} alt={detailListing.asset_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Hexagon size={64} className="text-[#627EEA] opacity-20" /></div>
            )}
          </div>

          <div>
            <h2 className="text-white font-serif text-xl">{detailListing.asset_name}</h2>
            <p className="text-gold text-2xl font-semibold mt-1">{formatCurrency(detailListing.total_value)}</p>
          </div>

          <div className="flex gap-3">
            <Tooltip><TooltipTrigger asChild><div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded cursor-help"><Shield size={12} className="text-emerald-400" /><span className="text-xs text-emerald-400">Verified</span></div></TooltipTrigger><TooltipContent className="bg-[#1A1A1A] border border-[#333] text-white max-w-[200px]"><p className="text-xs">Physically authenticated by certified specialists.</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><div className="flex items-center gap-1.5 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded cursor-help"><Lock size={12} className="text-gold" /><span className="text-xs text-gold">Insured</span></div></TooltipTrigger><TooltipContent className="bg-[#1A1A1A] border border-[#333] text-white max-w-[200px]"><p className="text-xs">Full replacement value covered by insurance.</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#627EEA]/10 border border-[#627EEA]/20 rounded cursor-help"><Globe size={12} className="text-[#627EEA]" /><span className="text-xs text-[#627EEA]">On-Chain</span></div></TooltipTrigger><TooltipContent className="bg-[#1A1A1A] border border-[#333] text-white max-w-[200px]"><p className="text-xs">Ownership verified on Ethereum blockchain.</p></TooltipContent></Tooltip>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-[10px] text-muted-foreground uppercase">Category</p><p className="text-white text-sm capitalize">{detailListing.category}</p></div>
            <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-[10px] text-muted-foreground uppercase">Condition</p><p className="text-white text-sm">{detailListing.condition || 'Excellent'}</p></div>
            <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-[10px] text-muted-foreground uppercase">Storage Location</p><p className="text-white text-sm">{detailListing.location || 'Secure Vault'}</p></div>
            <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-[10px] text-muted-foreground uppercase">Insurance</p><p className="text-white text-sm">{detailListing.insurance_provider || 'Insured'}</p></div>
          </div>

          {detailListing.description && (
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Description</p><p className="text-sm text-muted-foreground leading-relaxed">{detailListing.description}</p></div>
          )}

          <div className="p-4 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-muted-foreground">Price per fraction</span>
              <span className="text-gold font-semibold">{formatCurrency(detailListing.price_per_fraction)}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-muted-foreground">Available</span>
              <span className="text-white">{detailListing.fractions_available?.toLocaleString()} of {detailListing.total_fractions?.toLocaleString()}</span>
            </div>
            <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#627EEA] to-[#3B5998] rounded-full" style={{ width: `${((detailListing.fractions_available || 0) / (detailListing.total_fractions || 1)) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { setDetailListing(null); setBuyModal(detailListing); }} className="flex-1 min-w-[140px] bg-[#627EEA] hover:bg-[#5570D0] text-white" data-testid="detail-buy-btn">
              <ShoppingCart size={14} className="mr-2" /> Buy at list price
            </Button>
            <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="detail-make-offer-btn" onClick={() => {
              setOfferForm({ price: String(detailListing.price_per_fraction), fractions: '1', message: '' });
              setShowOfferModal(detailListing);
              setDetailListing(null);
            }}>
              <MessageSquare size={14} className="mr-2" /> Make Offer
            </Button>
            <Button variant="outline" className="border-[#627EEA]/30 text-[#627EEA] hover:bg-[#627EEA]/10" data-testid="detail-set-alert-btn" onClick={() => {
              setAlertForm({ target: String(Math.max(detailListing.price_per_fraction * 0.9, 1).toFixed(2)) });
              setShowAlertModal(detailListing);
              setDetailListing(null);
            }}>
              <Bell size={14} className="mr-2" /> Set Alert
            </Button>
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="contact-seller-btn" onClick={() => {
              if (!detailListing?.seller_id) { toast.error('Seller is unavailable.'); return; }
              setContactFor(detailListing);
              setContactMessage(`Hi, I'm interested in ${detailListing.asset_name}. Could we discuss?`);
              setDetailListing(null);
            }}>
              <MessageSquare size={14} className="mr-2" /> Contact Seller
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
